import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  emailReport,
  type EmailReportKind,
  type EmailReportPayload,
  type EmailReportTable,
} from '@/infrastructure/http/metricsApi';
import { rememberEmailContact } from '@/infrastructure/http/contactsApi';
import {
  EmailContactPicker,
  type PickedEmailRecipient,
} from '@/presentation/components/contacts/EmailContactPicker';
import { canExportReports } from '@/application/permissions/canExportReports';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';

export interface EmailReportScope {
  kind: EmailReportKind;
  year: number;
  month: number;
  tenantId?: string;
  title?: string;
  description?: string;
  kpis?: Array<{ label: string; value: string | number }>;
  /** Tablas de texto para kind=generic. */
  tables?: EmailReportTable[];
}

interface EmailReportButtonProps {
  scope: EmailReportScope;
  disabled?: boolean;
  className?: string;
}

/**
 * Botón «Enviar» + modal: genera PDF tipográfico (tablas) y lo adjunta por email.
 */
export function EmailReportButton({ scope, disabled, className }: EmailReportButtonProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { hasPermission } = usePermissions();
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState<PickedEmailRecipient[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!canExportReports(hasPermission)) {
    return null;
  }

  async function handleSend() {
    if (!token || recipients.length === 0) {
      return;
    }
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const to = recipients.map((r) => r.email).join(', ');
      const payload: EmailReportPayload = {
        to,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
        kind: scope.kind,
        year: scope.year,
        month: scope.month,
        tenantId: scope.tenantId,
        title: scope.title,
        description: scope.description,
        kpis: scope.kpis,
        tables: scope.tables,
      };

      const result = await emailReport(token, payload);

      await Promise.allSettled(
        recipients.map((r) =>
          rememberEmailContact(token, {
            nombre: r.nombre || r.email,
            email: r.email,
          })
        )
      );

      setSuccess(t('reports.email.success', { email: result.to }));
      setRecipients([]);
      setSubject('');
      setMessage('');
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      if (code === 'email_not_configured' || code === 'invalid_email') {
        setError(t(`reports.email.errors.${code}`, { defaultValue: t('reports.email.errors.generic') }));
      } else {
        setError(t('reports.email.errors.generic'));
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen(true);
          setRecipients([]);
          setError(null);
          setSuccess(null);
        }}
        className={className ?? 'btn btn-secondary'}
      >
        {t('reports.email.send')}
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-report-title"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <div className="bi-panel w-full max-w-lg">
              <h2 id="email-report-title" className="text-lg font-semibold tracking-tight text-gray-950 dark:text-white">
                {t('reports.email.title')}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('reports.email.hint')}</p>

              <div className="mt-4 space-y-3">
                {token ? (
                  <EmailContactPicker
                    key={scope.tenantId ?? 'all'}
                    token={token}
                    clinicId={scope.tenantId}
                    preselectTeam
                    selected={recipients}
                    onChange={setRecipients}
                    disabled={sending}
                  />
                ) : null}

                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('reports.email.subject')}
                  </span>
                  <input
                    type="text"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    placeholder={t('reports.email.subjectPlaceholder')}
                    className="h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-text-on-surface)] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t('reports.email.message')}
                  </span>
                  <textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    rows={3}
                    placeholder={t('reports.email.messagePlaceholder')}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-on-surface)] outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                  />
                </label>
              </div>

              {error && (
                <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
              {success && (
                <p role="status" className="mt-3 text-sm text-teal-700 dark:text-teal-300">
                  {success}
                </p>
              )}

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="btn btn-ghost"
                >
                  {t('common.close')}
                </button>
                <button
                  type="button"
                  disabled={sending || recipients.length === 0}
                  onClick={() => void handleSend()}
                  className="btn btn-primary"
                >
                  {sending ? t('reports.email.sending') : t('reports.email.confirm')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
